import { Station } from '@app/core/types/stations';
import {
  PntAppControllerSegmentsSimpleSearchQueryParams,
  pntAppControllerSegmentsSimpleSearchQueryKey,
  usePntAppControllerSegmentsSimpleSearch,
} from '@mi-front/api-v2';
import { DateTimeStr, TimeRange } from '@mi-front/core';

/** Elastic simple_query_string token that returns hour-bucketed transcriptions. */
const HOUR_BUCKET_QUERY = '_hour';

/**
 * Builds PNT simple-search params for logger stream hour-bucketed transcriptions.
 * `diarization` is sent as a string because the OpenAPI contract types it that way.
 */
function transcriptionsQueryParams(
  range: TimeRange<DateTimeStr>,
  diarization: boolean,
): PntAppControllerSegmentsSimpleSearchQueryParams {
  return {
    query: HOUR_BUCKET_QUERY,
    from: range.from,
    to: range.to,
    diarization: String(diarization),
  };
}

/**
 * Fetches hour-bucketed transcriptions (optional diarization) for a station range
 * via `usePntAppControllerSegmentsSimpleSearch`. Shared by diarization markers and
 * the transcriptions tab so identical params hit the same React Query cache.
 */
export function useLoggerStreamTranscriptions(
  station: Station,
  range: TimeRange<DateTimeStr>,
  diarization: boolean,
  options?: { enabled?: boolean },
) {
  const params = transcriptionsQueryParams(range, diarization);

  return usePntAppControllerSegmentsSimpleSearch(station.slug, params, {
    query: {
      enabled: (options?.enabled ?? true) && !!range?.from && !!range?.to,
    },
  });
}

useLoggerStreamTranscriptions.queryKey = (
  station: Station,
  range: TimeRange<DateTimeStr>,
  diarization: boolean,
) =>
  pntAppControllerSegmentsSimpleSearchQueryKey(
    station.slug,
    transcriptionsQueryParams(range, diarization),
  );
